import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Annex from '../components/Annex';
import LatestItemsSection from '../components/LatestItemsSection';
import Divider from '../components/Divider';
import { useLanguage } from '../context/LanguageContext'; // Import Language Context
import api from '../utils/api.js';

const Home = () => {
  const { language } = useLanguage(); // Get selected language from context
  const [audioDescriptions, setAudioDescriptions] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const [talksReading, setTalksReading] = useState([]);
  const [laboratories, setLaboratories] = useState([]);
  const [resources, setResources] = useState([]);
  const [friends, setFriends] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch data using the `api` instance
        const audioResponse = await api.get('/audiodescriptions');
        const laboratoriesResponse = await api.get('/laboratories');
        const talksResponse = await api.get('/talks_readings');
        const [articlesResponse, studiesResponse] = await Promise.all([
          api.get('/articles'),
          api.get('/studies'),
        ])

        const selectedLanguage = language.toLowerCase(); // Convert language to lowercase for consistency

        // Filter data based on `page_language`
        setAudioDescriptions(
          audioResponse.data
            .filter((item) => item.page_language[0].name.toLowerCase() === selectedLanguage)
            .slice(0, 3)
            .map((item) => ({
              id: item.id,
              title: item.title.rendered,
              description: item.description || item.excerpt.rendered || '',
              imgSrc: item.card_image.guid,
              imgAlt: item.card_image.post_excerpt,
              linkPath: `/resources/audio-description/${item.id}`,
            }))
        );

        setTalksReading(
          talksResponse.data
          .filter((item) => item.page_language[0].name.toLowerCase() === selectedLanguage)
          .slice(0, 3)
            .map((item) => ({
              id: item.id,
              title: item.title.rendered,
              description: item.description || item.excerpt.rendered || '',
              imgSrc: item.card_image.guid,
              imgAlt: item.card_image.post_excerpt,
              linkPath: `/resources/talks-reading/${item.id}`,
            }))
        );

        setLaboratories(
          laboratoriesResponse.data
          .filter((item) => item.page_language[0].name.toLowerCase() === selectedLanguage)
          .slice(0, 3)
            .map((item) => ({
              id: item.id,
              title: item.title.rendered,
              description: item.description,
              imgSrc: item.image.guid,
              imgAlt: item.image.post_excerpt,
              linkPath: `/resources/laboratories/${item.id}`,
            }))
        );

        const combinedResources = [
          ...articlesResponse.data
          .filter((item) => item.page_language[0].name.toLowerCase() === selectedLanguage)
          .map((item) => ({
              id: item.id,
              title: item.title.rendered,
              description: item.description || item.excerpt.rendered || '',
              imgSrc: item.main_image?.guid || '/path/to/default-image.jpg',
              imgAlt: item.main_image.post_excerpt,
              linkPath: `/resources/articles/${item.id}`,
              category: `articles`,
            })),
          ...studiesResponse.data
          .filter((item) => item.study_language[0].name.toLowerCase() === selectedLanguage)
          .map((item) => ({
              id: item.id,
              title: item.title.rendered,
              description: item.description || '',
              imgSrc: item.main_image?.guid || '/path/to/default-image.jpg',
              imgAlt: item.main_image.post_excerpt,
              linkPath: `/resources/studies/${item.id}`,
              category: `studies`,
            })),
        ].slice(0, 6); // Limit to the first 6 items

        setResources(combinedResources);
      } catch (error) {
        console.error('Error fetching homepage sections:', error);
      }
    };

    fetchData();
  }, [language]); // Re-run the effect whenever the selected language changes

  return (
    <div>
      <Annex />
      <Divider />

      {/* Audio Descriptions Section */}
      {audioDescriptions.length > 0 ? (
        <LatestItemsSection
          title={language === 'Shqip' ? 'Përshkrimet Zanore' : 'Audio Descriptions'}
          items={audioDescriptions}
          linkPath="/artistic-program/audio-descriptions"
          linkText={language === 'Shqip' ? 'Shiko të gjitha Përshkrimet Zanore' : 'View All Audio Descriptions'}
        />
      ) : (
        <div className="container mx-auto my-12 px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">{language === 'Shqip' ? 'Përshkrimet Zanore' : 'Audio Descriptions'}</h2>
          <p className="text-gray-500">
            {language === 'Shqip' ? 'Nuk ka përshkrime zanore për momentin.' : 'No audio descriptions available at the moment.'}
          </p>
        </div>
      )}
      <Divider />

      {/* Laboratories Section */}
      {laboratories.length > 0 ? (
        <LatestItemsSection
          title={language === 'Shqip' ? 'Laboratorët' : 'Laboratories'}
          items={laboratories}
          linkPath="/educational-program/laboratories"
          linkText={language === 'Shqip' ? 'Shiko të gjithë Laboratorët' : 'View All Laboratories'}
        />
      ) : (
        <div className="container mx-auto my-12 px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">{language === 'sq' ? 'Laboratorët' : 'Laboratories'}</h2>
          <p className="text-gray-500">
            {language === 'Shqip' ? 'Nuk ka laboratorë për momentin.' : 'No laboratories available at the moment.'}
          </p>
        </div>
      )}
      <Divider />

      {/* Talks and Reading Sessions Section */}
      {talksReading.length > 0 ? (
        <LatestItemsSection
          title={language === 'Shqip' ? 'Bisedat dhe Seancat e Leximit' : 'Talks and Reading Sessions'}
          items={talksReading}
          linkPath="/educational-program/talks_reading"
          linkText={language === 'Shqip' ? 'Shiko të gjitha Bisedat dhe Seancat e Leximit' : 'View All Talks and Reading Sessions'}
        />
      ) : (
        <div className="container mx-auto my-12 px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">{language === 'Shqip' ? 'Bisedat dhe Seancat e Leximit' : 'Talks and Reading Sessions'}</h2>
          <p className="text-gray-500">
            {language === 'Shqip' ? 'Nuk ka informata për momentin.' : 'No information available at the moment.'}
          </p>
        </div>
      )}
      <Divider />

      {/* Resources Section */}
      {resources.length > 0 ? (
        <LatestItemsSection
          title={language === 'Shqip' ? 'Resurset' : 'Resources'}
          items={resources}
          linkPath="/resources"
          linkText={language === 'Shqip' ? 'Shiko të gjitha resurset' : 'View All Resources'}
        />
      ) : (
        <div className="container mx-auto my-12 px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">{language === 'Shqip' ? 'Resurset' : 'Resources'}</h2>
          <p className="text-gray-500">
            {language === 'Shqip' ? 'Nuk ka resurse për momentin.' : 'No resources available at the moment.'}
          </p>
        </div>
      )}
      <Divider />

      <Footer />
    </div>
  );
};

export default Home;